import { apiGet, apiPost } from './api-client.js';

/**
 * Typed wrappers over `POST/GET /auth/*` (API_CONTRACT.md §13.1). This is the ONLY file
 * that knows these paths — routes/components call these functions, never `apiPost`
 * directly, so the contract's shape lives in one place.
 */

/**
 * These shapes were VERIFIED against a running `internal-api`, not inferred from prose.
 *
 * The earlier versions were inferred (A-042) and were wrong in three ways: the server
 * sends `fullName`, not `displayName`; roles are `{roleCode, scopeLevel,
 * maxClearanceRank}`, not `{code, labelEn, labelNe}`; and `/auth/login` returns role
 * CODES as plain strings while `/auth/me` returns objects. None of it threw — the
 * dashboard simply rendered "Welcome, {name}" and "Roles: —", which is what a field-name
 * mismatch looks like in React: empty, not broken.
 */
export interface SessionUser {
  id: string;
  username: string;
  fullName: string;
  locale: 'en' | 'ne';
  mustChangePassword?: boolean;
}

/** `/auth/me`'s role shape. Labels are NOT returned; the client renders the code. */
export interface SessionRole {
  roleCode: string;
  scopeLevel: string;
  maxClearanceRank: number;
}

export interface MeResponse {
  user: SessionUser;
  roles: SessionRole[];
  permissions: string[];
  tenantId: string;
  sessionId: string;
}

export interface LoginRequest {
  /**
   * The ministry's tracking-code prefix — 2-6 letters, e.g. `MOHA`.
   *
   * Required because usernames are unique PER MINISTRY, not platform-wide: `officer`
   * exists in more than one tenant, so username and password alone do not identify an
   * account. API_CONTRACT §13.1 originally documented `{username, password}`; that was
   * written before tenancy was settled and could never have worked.
   */
  codePrefix: string;
  username: string;
  password: string;
}

/**
 * `/auth/login` returns LESS than `/auth/me`: the user id only, and roles as bare code
 * strings. The full session is read from `/auth/me` after the redirect, which is why the
 * dashboard has everything it needs despite this being thin.
 */
export interface LoginResponse {
  user: { id: string };
  roles: string[];
  mustChangePassword: boolean;
}

export function login(body: LoginRequest): Promise<LoginResponse> {
  return apiPost<LoginResponse>('/auth/login', body);
}

export interface MfaVerifyRequest {
  mfaChallengeId: string;
  code: string;
}

/**
 * `/auth/mfa/verify` answers `{sessionId}` (`auth-routes.ts`), NOT the `LoginResponse`
 * shape `/auth/login` returns — this was typed as `LoginResponse` even though nothing on
 * that interface (`user`, `roles`, `mustChangePassword`) is ever present on the wire here.
 * `MfaPage.tsx` never reads the resolved value (it invalidates `/auth/me` and navigates
 * instead), which is why the wrong type compiled without ever throwing — fixed to the
 * shape actually observed on the running server.
 */
export interface MfaVerifyResponse {
  sessionId: string;
}

export function verifyMfa(body: MfaVerifyRequest): Promise<MfaVerifyResponse> {
  return apiPost<MfaVerifyResponse>('/auth/mfa/verify', body);
}

export function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  return apiGet<MeResponse>('/auth/me', signal);
}

/** `auth-routes.ts` answers `{loggedOut: true}`, not an empty body — this was typed `Promise<null>` and no caller reads the result, so the mismatch never showed, but the type should say what the server actually sends. */
export function logout(): Promise<{ loggedOut: true }> {
  return apiPost<{ loggedOut: true }>('/auth/logout');
}

/**
 * `POST /auth/password/change` (API_CONTRACT.md §13.1: "any session",
 * `{currentPassword, newPassword}`). SCREEN_SPECS.md I23 names this the one gap worth
 * closing over polish, and I3 (forced first-login change) has nothing to act on without
 * it — `AccountSettingsPage` wires both to this one function.
 *
 * **R-040's status, re-verified live at the START of this pass, not assumed stale**: the
 * route now EXISTS in `auth-routes.ts` (a backend agent landed it while this pass was
 * underway — confirmed by reading the source, which declares
 * `{changed: true, sessionsRevoked}` on success), but the RUNNING `internal-api` process
 * this build drove against still answered `404 NOT_FOUND` on a direct request at the time
 * of writing — the source and the running process had not yet converged. This function is
 * typed to the real response shape now visible in source so the client is correct the
 * moment the process picks it up; `AccountSettingsPage`'s existing 404 fallback (built by
 * an earlier pass) still renders correctly in the meantime, and should be revisited once
 * the route is confirmed live end-to-end.
 */
export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResult {
  changed: true;
  sessionsRevoked: number;
}

export function changePassword(input: ChangePasswordInput): Promise<ChangePasswordResult> {
  return apiPost<ChangePasswordResult>('/auth/password/change', input);
}

/**
 * `POST /auth/password/reset-request` (SCREEN_SPECS.md I4's request step) — live on this
 * server (unlike `/password/change`, above). **Contract divergence, verified against the
 * running route (`auth-routes.ts`'s `resetRequestSchema`), not assumed from prose**:
 * SCREEN_SPECS.md I4 calls this "email-based" and specs an email input, but the server
 * accepts `{codePrefix, username}` — the SAME two identifying fields as I1's login form —
 * and has no email field anywhere in its schema. This screen is built against what the
 * server actually takes, flagged in the handback as a spec/server mismatch rather than
 * silently building an email field that would 422 on every submission.
 *
 * Always answers `{accepted: true}` — API_CONTRACT §13.1: "always the same response,
 * whether or not the account exists" (the same enumeration-oracle discipline as login).
 */
export interface RequestPasswordResetInput {
  codePrefix: string;
  username: string;
}

export function requestPasswordReset(input: RequestPasswordResetInput): Promise<{ accepted: true }> {
  return apiPost<{ accepted: true }>('/auth/password/reset-request', input);
}

/**
 * `POST /auth/password/reset` — the completion step. `404 NOT_FOUND` covers an invalid,
 * expired, AND already-used token alike (the same generic-failure discipline as login),
 * `422 VALIDATION_FAILED` with `fieldErrors` on `newPassword` for a policy violation.
 * `sessionsRevoked` confirms every existing session for the account was logged out, so the
 * UI can say so rather than leaving the user to wonder whether an old session is still live.
 */
export interface CompletePasswordResetInput {
  token: string;
  newPassword: string;
}

export interface CompletePasswordResetResult {
  reset: true;
  sessionsRevoked: number;
}

export function completePasswordReset(input: CompletePasswordResetInput): Promise<CompletePasswordResetResult> {
  return apiPost<CompletePasswordResetResult>('/auth/password/reset', input);
}

/**
 * `POST /auth/mfa/enrol` — R-033's redeem step (API_CONTRACT §13.1). Anonymous, allowlisted:
 * this is reachable ONLY by someone who cannot obtain a session at all (`secretary` and
 * `super_admin` require TOTP by role on every login, so a freshly created account of
 * either kind has no secret and no way to get one otherwise).
 *
 * **Deliberately repeatable within the token's one-hour life** — the server returns the
 * SAME secret on a second call, so a page refresh mid-scan does not invalidate the QR the
 * user is looking at. Never treat this as single-use on the client (no "already redeemed"
 * local state, no disabling a retry after the first success).
 *
 * `404 NOT_FOUND` covers an invalid, expired, or already-confirmed token alike — the same
 * generic-failure discipline as `/auth/password/reset`.
 */
export interface BeginMfaEnrolmentInput {
  token: string;
}

export interface BeginMfaEnrolmentResult {
  username: string;
  secretBase32: string;
  enrolmentUri: string;
  expiresAt: string;
}

export function beginMfaEnrolment(input: BeginMfaEnrolmentInput): Promise<BeginMfaEnrolmentResult> {
  return apiPost<BeginMfaEnrolmentResult>('/auth/mfa/enrol', input);
}

/**
 * `POST /auth/mfa/enrol/confirm` — the completion step. Only on `{enrolled: true}` does
 * the secret become live on the account; until then it lives only on the (still anonymous,
 * still allowlisted) token row. `AUTH_MFA_INVALID` means the code was wrong (retry with the
 * same QR/secret); `NOT_FOUND` means the token itself is gone (expired/used/invalid) and a
 * fresh one must come from an administrator — these are DIFFERENT failures with different
 * next actions and must not be collapsed into one message.
 */
export interface ConfirmMfaEnrolmentInput {
  token: string;
  code: string;
}

export interface ConfirmMfaEnrolmentResult {
  enrolled: true;
}

export function confirmMfaEnrolment(input: ConfirmMfaEnrolmentInput): Promise<ConfirmMfaEnrolmentResult> {
  return apiPost<ConfirmMfaEnrolmentResult>('/auth/mfa/enrol/confirm', input);
}

export { ApiError, NetworkError } from './api-client.js';
